import { z } from 'zod';
import { PLATFORM_VARIABLES } from '../../lib/platform-variables';
import { SHOW_ORG_LOGO_CHOICES } from '../../lib/show-org-logo';
import { consentConfigSchema } from '../../lib/consent-fields';

/** Value types a variable can hold — mirrors the API's VariableType enum. */
export const VARIABLE_TYPES = ['TEXT', 'NUMBER', 'CURRENCY', 'DATE'] as const;
export type VariableType = (typeof VARIABLE_TYPES)[number];

export const VARIABLE_TYPE_LABELS: Record<VariableType, string> = {
  TEXT: 'Text',
  NUMBER: 'Number',
  CURRENCY: 'Currency',
  DATE: 'Date',
};

const RESERVED_KEYS: string[] = PLATFORM_VARIABLES.map((v) => v.key);
const KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

function isNumeric(value: string): boolean {
  return value.trim() !== '' && Number.isFinite(Number(value));
}

/**
 * One declared input variable. `description`/`defaultValue` are always strings
 * in form state (never undefined) so their inputs stay controlled; the container
 * maps empty strings to undefined before sending to the API.
 */
export const templateVariableSchema = z
  .object({
    key: z
      .string()
      .min(1, 'Key is required')
      .regex(KEY_PATTERN, 'Use snake_case (a-z, 0-9, _)')
      .refine((k) => !RESERVED_KEYS.includes(k), 'This key is reserved by the platform')
      .refine((k) => !/^(system__|secret__)/.test(k), 'This prefix is reserved'),
    name: z.string().min(1, 'Name is required').max(120),
    type: z.enum(VARIABLE_TYPES),
    required: z.boolean(),
    description: z.string().max(2000),
    allowedValues: z.array(z.string().min(1)),
    defaultValue: z.string().max(500),
  })
  .superRefine((v, ctx) => {
    // Allowed values must match the type (mirrors the server) so authors get
    // inline feedback instead of a generic 400.
    const typedErr =
      v.type === 'NUMBER' || v.type === 'CURRENCY'
        ? { test: isNumeric, message: 'Allowed values must be numbers for this type' }
        : v.type === 'DATE'
          ? {
              test: (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s),
              message: 'Allowed values must be dates (YYYY-MM-DD)',
            }
          : null;
    if (typedErr && v.allowedValues.some((val) => !typedErr.test(val))) {
      ctx.addIssue({ code: 'custom', message: typedErr.message, path: ['allowedValues'] });
    }
    if (v.defaultValue && v.allowedValues.length > 0 && !v.allowedValues.includes(v.defaultValue)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Default must be one of the allowed values',
        path: ['defaultValue'],
      });
    }
    if (
      v.defaultValue &&
      (v.type === 'NUMBER' || v.type === 'CURRENCY') &&
      !isNumeric(v.defaultValue)
    ) {
      ctx.addIssue({ code: 'custom', message: 'Default must be a number', path: ['defaultValue'] });
    }
    if (v.defaultValue && v.type === 'DATE' && !/^\d{4}-\d{2}-\d{2}$/.test(v.defaultValue)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Default must be a date (YYYY-MM-DD)',
        path: ['defaultValue'],
      });
    }
  });

export type TemplateVariableValue = z.infer<typeof templateVariableSchema>;

export const createInterviewTypeVersionFormSchema = z.object({
  // The template's own ElevenLabs agent prompt (pass-through — read live and
  // written live, never stored/versioned in our DB). Edited here alongside the
  // versioned config so everything is configured in one place; saving PATCHes
  // the live agent. `promptLoaded` is true only when the live read succeeded, so
  // the wipe-guard (`editAgentPromptFieldsForSubmit`) never submits '' and blanks
  // the agent — mirrors the create form.
  systemPrompt: z.string(),
  firstMessage: z.string(),
  promptLoaded: z.boolean(),
  // 'none' means "None" — containers map 'none' → undefined before sending to the API
  extractionVersionId: z.string(),
  rubricVersionId: z.string(),
  candidateTitle: z.string().min(1, 'Title is required').max(120),
  candidateDescription: z.string().min(1, 'Description is required').max(2000),
  candidateEndOfCallNotes: z.string().min(1, 'End-of-call notes are required').max(2000),
  candidateDeclinedMessage: z.string().min(1, 'Declined message is required').max(2000),
  candidateShowOrgLogo: z.enum(SHOW_ORG_LOGO_CHOICES),
  consentConfig: consentConfigSchema,
  dynamicVariableDefinitions: z
    .array(templateVariableSchema)
    .max(20, 'At most 20 variables')
    .superRefine((defs, ctx) => {
      const seen = new Set<string>();
      defs.forEach((def, index) => {
        if (seen.has(def.key)) {
          ctx.addIssue({
            code: 'custom',
            message: `Duplicate key "${def.key}"`,
            path: [index, 'key'],
          });
        }
        seen.add(def.key);
      });
    }),
});

export type CreateInterviewTypeVersionFormValues = z.infer<
  typeof createInterviewTypeVersionFormSchema
>;
